import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Palette, Save, RotateCcw, X } from 'lucide-react';


const CalendarColorSettingsModal = ({ isOpen, onClose, currentColors, onColorsUpdate }) => {
  const [colorSettings, setColorSettings] = useState(currentColors);
  const [originalSettings, setOriginalSettings] = useState(currentColors);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setColorSettings(currentColors);
      setOriginalSettings(currentColors);
    }
  }, [isOpen, currentColors]);

  const handleColorChange = (key, value) => {
    setColorSettings({
      ...colorSettings,
      [key]: value
    });
  };

  const saveColorSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      
      await api.put(`/users/calendar-colors`, colorSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOriginalSettings(colorSettings);
      onColorsUpdate(colorSettings);
      setMessage('Color settings saved successfully!');
      
      // Trigger calendar refresh
      window.dispatchEvent(new CustomEvent('calendarColorsUpdated', { 
        detail: colorSettings 
      }));
      
      // Clear message after 2 seconds, then close
      setTimeout(() => {
        setMessage('');
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error saving color settings:', error);
      setMessage('Error saving color settings');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    const defaults = {
      task_critical: '#DC2626',
      task_high: '#D97706', 
      task_normal: '#6B46C1',
      task_low: '#059669',
      task_overdue: '#B91C1C',
      meeting: '#059669',
      background: '#F9FAFB'
    };
    setColorSettings(defaults);
  };

  const resetToOriginal = () => {
    setColorSettings(originalSettings);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Palette className="w-6 h-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Calendar Colors</h2>
              <p className="text-gray-600">Customize your schedule colors</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {message && (
            <div className={`mb-4 p-4 rounded-lg ${
              message.includes('Error') 
                ? 'bg-red-50 text-red-800 border border-red-200' 
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}>
              {message}
            </div>
          )}

          <div className="space-y-6">
            {/* Task Priority Colors */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Task Priority Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Critical Tasks</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.task_critical}
                      onChange={(e) => handleColorChange('task_critical', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.task_critical}
                      onChange={(e) => handleColorChange('task_critical', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#DC2626"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">High Priority</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.task_high}
                      onChange={(e) => handleColorChange('task_high', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.task_high}
                      onChange={(e) => handleColorChange('task_high', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#D97706"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Normal Priority</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.task_normal}
                      onChange={(e) => handleColorChange('task_normal', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.task_normal}
                      onChange={(e) => handleColorChange('task_normal', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#6B46C1"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Low Priority</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.task_low}
                      onChange={(e) => handleColorChange('task_low', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.task_low}
                      onChange={(e) => handleColorChange('task_low', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#059669"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Special Task States */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Special States</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Overdue Tasks</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.task_overdue}
                      onChange={(e) => handleColorChange('task_overdue', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.task_overdue}
                      onChange={(e) => handleColorChange('task_overdue', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#B91C1C"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meetings</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.meeting}
                      onChange={(e) => handleColorChange('meeting', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.meeting}
                      onChange={(e) => handleColorChange('meeting', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#059669"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Calendar Background</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colorSettings.background}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorSettings.background}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="#F9FAFB"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="p-3 rounded-lg text-white text-center text-sm" style={{ backgroundColor: colorSettings.task_critical }}>
                  Critical
                </div>
                <div className="p-3 rounded-lg text-white text-center text-sm" style={{ backgroundColor: colorSettings.task_high }}>
                  High
                </div>
                <div className="p-3 rounded-lg text-white text-center text-sm" style={{ backgroundColor: colorSettings.task_normal }}>
                  Normal
                </div>
                <div className="p-3 rounded-lg text-white text-center text-sm" style={{ backgroundColor: colorSettings.task_low }}>
                  Low
                </div>
                <div className="p-3 rounded-lg text-white text-center text-sm" style={{ backgroundColor: colorSettings.task_overdue }}>
                  Overdue
                </div>
                <div className="p-3 rounded-lg text-white text-center text-sm" style={{ backgroundColor: colorSettings.meeting }}>
                  Meeting
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              onClick={resetToDefaults}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset to Defaults</span>
            </button>
            
            <button
              onClick={resetToOriginal}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel Changes
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
            <button
              onClick={saveColorSettings}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Colors'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarColorSettingsModal;